import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../store/store";
import useExcelDownload from "../../../hooks/useExcelDownload";
import { stringToArray } from "../../../utils/array";
import { sliceString } from "../../../utils/string";
import * as XLSX from "xlsx";
import { BASE_API } from "../../../helpers/apiUrl";
import AXIOS from "../../../helpers/api";
import { GetAllTowerData } from "../../../types/redux";
import { capitalize } from "lodash";
import { formatDate } from "../../../utils/time";
import { toast } from "../../../helpers/toast";
import moment from "moment";
import { uploadTicketExcelAsync } from "../../../store/features/NP/network/networkAPI";
import { getAllUsersAsync } from "../../../store/features/NP/resource/resourceAPI";
import CustomModal from "../../common/CustomModal";
import Dragger from "antd/es/upload/Dragger";
import { Button } from "antd";
import { irExcelUploadAsync } from "../../../store/features/NP/supplier/supplierAPI";

type Props = {
  open: boolean;
  handleClose: () => void;
  data?: any;
  reRender: (val: boolean) => void;
  uploadedCount: (val: number, total: number) => void;
};

export default function ImportFinanceExcel({
  open,
  handleClose,
  data,
  reRender,
  uploadedCount,
}: Props) {
  const dispatch = useAppDispatch();
  const [fileName, setFileName] = useState("No file chosen");
  const [loading, setLoading] = useState(false);
  const [uploadedData, setUploadedData] = useState<any[]>([]);
  const { excelLoading, downloadExcel, handleExcelLoading } =
    useExcelDownload("IR-Excel");
  const [uploadLoading, setUploadLoading] = useState(false);
  const { postLoading } = useAppSelector((state) => state.supplier);

  const handleUploadFile = (file: any) => {
    if (
      file?.name?.endsWith(".xls") ||
      file?.name?.endsWith(".xlsx") ||
      file?.type === "application/vnd.ms-excel" ||
      file?.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      setLoading(true);
      const reader = new FileReader();
      reader.readAsBinaryString(file);

      const fileNameArr = stringToArray(file?.name, ".");
      setFileName(
        `${sliceString(file?.name, 30, "...")}.${
          fileNameArr?.[fileNameArr?.length - 1]
        }`
      );

      reader.onload = (e) => {
        const data = e?.target?.result;
        const workBook = XLSX.read(data, {
          type: "binary",
          cellDates: true,
          dateNF: "mm/dd/yyyy",
        });
        const sheetName = workBook.SheetNames[0];
        const sheet = workBook.Sheets[sheetName];
        const parsedData = XLSX.utils.sheet_to_json(sheet, { raw: false });

        const filteredData = parsedData.map((el: any, index: number) => {
          const trackingNumberRaw =
            el?.["trackingNumber"]?.trim() ||
            el?.["Tracking Number"]?.trim() ||
            "";

          const invoiceNumberRaw =
            el?.["invoiceNumber"]?.trim() ||
            el?.["Invoice Number (D365)"]?.trim() ||
            "";
          const jbn = el?.["jbn"]?.trim() || el?.["JVN"]?.trim() || "";
          const dateRaw = el?.["date"]?.trim() || el?.["Date"]?.trim() || "";

          // Check if tracking number is numeric and negative
          if (
            trackingNumberRaw &&
            !isNaN(trackingNumberRaw) &&
            Number(trackingNumberRaw) < 0
          ) {
            toast(
              "error",
              `Row ${index + 1}: Tracking Number can never be negative`
            );
            return null;
          }

          // Validation: Tracking Number cannot be decimal
          if (
            trackingNumberRaw &&
            !isNaN(trackingNumberRaw) &&
            Number(trackingNumberRaw) % 1 !== 0
          ) {
            toast(
              "error",
              `Row ${index + 1}: Tracking Number can never be decimal`
            );
            return null;
          }

          // Validation: Tracking Number must be in format: digits-LETTERS (e.g. 12345-TAMIMI)
          if (trackingNumberRaw && !/^\d+-[a-zA-Z]+$/.test(trackingNumberRaw)) {
            toast(
              "error",
              `Row ${index + 1}: Please provide a valid tracking number`
            );
            return null;
          }

          //  Validation: Invoice Number cannot be negative
          if (
            invoiceNumberRaw &&
            !isNaN(invoiceNumberRaw) &&
            Number(invoiceNumberRaw) < 0
          ) {
            toast(
              "error",
              `Row ${index + 1}: Invoice Number can never be negative`
            );
            return null;
          }

          // Validation: Invoice Number cannot be decimal
          if (
            invoiceNumberRaw &&
            !isNaN(invoiceNumberRaw) &&
            Number(invoiceNumberRaw) % 1 !== 0
          ) {
            toast(
              "error",
              `Row ${index + 1}: Invoice Number can never be decimal`
            );
            return null;
          }

          // Validation: Invoice Number must be digits only
          if (invoiceNumberRaw && !/^\d+-[a-zA-Z]+$/.test(invoiceNumberRaw)) {
            toast(
              "error",
              `Row ${
                index + 1
              }: Please provide a valid invoice number(e.g 1222-name)`
            );
            return null;
          }

          //  Validation: Date cannot be in the past
          if (dateRaw) {
            const inputDate = new Date(dateRaw);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (inputDate < today) {
              toast("error", `Row ${index + 1}: Date can never be a past date`);
              return null;
            }
          }

          return {
            trackingNumber: trackingNumberRaw,
            invoiceNumber: invoiceNumberRaw,
            jbn: jbn,
            date: dateRaw,
          };
        });

        const validData = filteredData.filter((item) => item !== null);
        setUploadedData(validData);
        setLoading(false);
      };
    } else {
      setLoading(false);
      toast("error", "Please upload only excel file");
    }
  };

  const handleDownloadExcel = () => {
    handleExcelLoading(true);

    const excelData = [
      {
        "Tracking Number": "",
        JVN: "",
        "Invoice Number (D365)": "",
        Date: "",
      },
    ];

    downloadExcel(excelData);
    handleExcelLoading(false);
  };

  const handleUploadExcel = () => {
    setUploadLoading(true);
    const newData = {
      irDataArray: [...uploadedData],
    };

    if (uploadedData.length > 0) {
      dispatch(
        irExcelUploadAsync({
          data: newData,
          others: {
            reRender,
          },
        })
      );
      setUploadLoading(false);
    }
  };

  useEffect(() => {
    dispatch(getAllUsersAsync({}));
  }, [dispatch]);

  return (
    <CustomModal
      width={600}
      title="Excel Sheet"
      open={open}
      handleClose={!uploadLoading && !postLoading ? handleClose : () => {}}
    >
      <div className="p-6">
        <label className="text-[#3d4a5c] text-sm font-medium leading-normal mb-1.5 inline-block">
          Import Excel File
        </label>
        <Dragger
          name="file"
          action=""
          accept=".xlsx, .xls"
          maxCount={1}
          showUploadList={false}
          beforeUpload={() => false}
          onChange={(info) => handleUploadFile(info.file)}
          onDrop={(e) => handleUploadFile(e.dataTransfer.files[0])}
          // excelPostLoading ||
          disabled={loading || uploadLoading || postLoading}
          className="import_excel"
        >
          <Button
            className="flex justify-between items-center"
            size="large"
            disabled={loading || uploadLoading || postLoading}
            style={{ width: "100%" }}
          >
            <div className="flex items-center gap-2 text-sm font-normal font-roboto text-[#8797ae]">
              <span className="text-accent font-medium text-base leading-7 relative">
                Choose File
              </span>
              <span className="border-r border-solid border-l-0 border-y-0 border-others-border h-4" />
              {loading ? "Uploading..." : fileName}
            </div>
          </Button>
        </Dragger>

        <div className="px-4 py-[9px] bg-[#fffbe6] rounded-md border border-[#ffe58f] text-sm text-[#874d00] mt-4">
          <span className="font-medium">Warning! </span>
          <span>
            Upload only xlsx file. For any blank column put N/A, otherwise data
            will be mismatch. Your can see sample file,{" "}
          </span>
          <button
            className="text-[#1890ff] cursor-pointer text-sm font-medium bg-transparent border-none"
            onClick={handleDownloadExcel}
            disabled={excelLoading}
          >
            click here
          </button>
        </div>

        <div className="flex justify-center mt-7">
          <Button
            size="large"
            type="primary"
            className="w-72 !h-12"
            onClick={handleUploadExcel}
            disabled={postLoading || uploadedData?.length === 0}
            loading={postLoading}
          >
            Upload
          </Button>
        </div>
      </div>
    </CustomModal>
  );
}
